import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ContentDivisionActions } from './ContentDivisionActions';
import {
  FileText, 
  Scissors, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Loader2,
  Eye,
  Edit,
  Save,
  Sparkles
} from 'lucide-react';
import { ContentDivisionService, DivisionStatus } from '@/services/contentDivisionService';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ModuleForDivision {
  module_id: string;
  course_id: string;
  module_title: string;
  course_title: string;
  word_count: number;
}

export const ContentDivisionDashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [extremeModules, setExtremeModules] = useState<DivisionStatus[]>([]);
  const [heavyModules, setHeavyModules] = useState<DivisionStatus[]>([]);
  const [moderateModules, setModerateModules] = useState<DivisionStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState<ModuleForDivision | null>(null);
  const [moduleContent, setModuleContent] = useState<string>('');
  const [detectedSections, setDetectedSections] = useState<any[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [statsData, extreme, heavy, moderate] = await Promise.all([
        ContentDivisionService.getDivisionStats(),
        ContentDivisionService.getModulesByTier('extreme'),
        ContentDivisionService.getModulesByTier('heavy'),
        ContentDivisionService.getModulesByTier('moderate')
      ]);

      setStats(statsData);
      setExtremeModules(extreme);
      setHeavyModules(heavy);
      setModerateModules(moderate);
    } catch (error) {
      logger.error('Failed to fetch division dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load content division data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewModule = async (module: DivisionStatus) => {
    try {
      // Fetch module content from database
      const { data, error } = await supabase
        .from('module_content')
        .select('content, module_id, course_id, content_word_count')
        .eq('module_id', module.module_id)
        .eq('course_id', module.course_id)
        .single();

      if (error) throw error;

      if (data) {
        setSelectedModule({
          module_id: data.module_id,
          course_id: data.course_id,
          module_title: module.module_id, // Use module_id as title for now
          course_title: module.course_id, // Use course_id as title for now
          word_count: data.content_word_count
        });
        setModuleContent(data.content);
        
        // Auto-detect sections
        const sections = ContentDivisionService.autoDetectSections(data.content);
        setDetectedSections(sections);
      }
    } catch (error) {
      logger.error('Failed to preview module:', error);
      toast({
        title: 'Error',
        description: 'Failed to load module content',
        variant: 'destructive'
      });
    }
  };

  const handleSaveSections = async () => {
    if (!selectedModule || detectedSections.length === 0) return;

    try {
      const sections = detectedSections.map((section, index) => ({
        module_id: selectedModule.module_id,
        course_id: selectedModule.course_id,
        section_id: section.title.toLowerCase().replace(/\s+/g, '-'),
        section_title: section.title,
        section_content: section.content,
        section_order: index,
        word_count: section.estimatedWords
      }));

      const success = await ContentDivisionService.saveSections(
        selectedModule.module_id,
        selectedModule.course_id,
        sections
      );

      if (success) {
        // Update status
        await ContentDivisionService.updateStatus(
          selectedModule.module_id,
          selectedModule.course_id,
          {
            num_sections: sections.length,
            status: 'completed',
            completed_at: new Date().toISOString(),
            processing_method: 'manual'
          }
        );

        toast({
          title: 'Success',
          description: `Divided module into ${sections.length} sections`,
        });

        setSelectedModule(null);
        fetchData(); // Refresh dashboard
      } else {
        throw new Error('Failed to save sections');
      }
    } catch (error) {
      logger.error('Failed to save sections:', error);
      toast({
        title: 'Error',
        description: 'Failed to save divided content',
        variant: 'destructive'
      });
    }
  };

  const renderModulesList = (modules: DivisionStatus[], tier: string) => {
    if (modules.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No modules in {tier} tier</p>
        </div>
      );
    }

    return (
      <ScrollArea className="h-[600px]">
        <div className="space-y-3 pr-4">
          {modules.map((module) => (
            <Card key={`${module.module_id}-${module.course_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold mb-1">{module.module_id}</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      {module.course_id}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">
                        {module.original_word_count.toLocaleString()} words
                      </Badge>
                      {module.status === 'completed' ? (
                        <Badge variant="default" className="bg-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {module.num_sections} sections
                        </Badge>
                      ) : module.status === 'processing' ? (
                        <Badge variant="secondary">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Processing
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handlePreviewModule(module)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {module.status === 'completed' ? 'View' : 'Divide'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                      <DialogHeader>
                        <DialogTitle>
                          {selectedModule?.module_title}
                        </DialogTitle>
                        <DialogDescription>
                          {selectedModule?.word_count.toLocaleString()} words • {detectedSections.length} sections detected
                        </DialogDescription>
                      </DialogHeader>
                      <ScrollArea className="flex-1 pr-4">
                        <div className="space-y-4">
                          {detectedSections.map((section, index) => (
                            <Card key={index}>
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-base">
                                    Section {index + 1}: {section.title}
                                  </CardTitle>
                                  <Badge variant="outline">
                                    {section.estimatedWords} words
                                  </Badge>
                                </div>
                              </CardHeader>
                              <CardContent>
                                <Textarea
                                  value={section.content}
                                  onChange={(e) => {
                                    const updated = [...detectedSections];
                                    updated[index].content = e.target.value;
                                    setDetectedSections(updated);
                                  }}
                                  className="min-h-[200px] font-mono text-sm"
                                />
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                      <div className="flex justify-end gap-2 pt-4 border-t">
                        <Button variant="outline" onClick={() => setSelectedModule(null)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveSections}>
                          <Save className="h-4 w-4 mr-2" />
                          Save {detectedSections.length} Sections
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase 2: Populate Database */}
      <ContentDivisionActions onRefresh={fetchData} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5" />
            Content Division Dashboard
          </CardTitle>
          <CardDescription>
            Manage module content division for {stats ? stats.overall.total : 784} modules with 2000+ words
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-muted-foreground">Total Modules</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.overall.total}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-muted-foreground">Completed</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {stats.overall.completed}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {((stats.overall.completed / stats.overall.total) * 100).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-muted-foreground">In Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">
                    {stats.overall.processing}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-muted-foreground">Pending</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-600">
                    {stats.overall.pending}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="extreme" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="extreme" className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            Extreme ({stats?.byTier.extreme.total || 16})
            {stats && stats.byTier.extreme.pending > 0 && (
              <Badge variant="destructive">{stats.byTier.extreme.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="heavy" className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-orange-500" />
            Heavy ({stats?.byTier.heavy.total || 147})
            {stats && stats.byTier.heavy.pending > 0 && (
              <Badge variant="secondary">{stats.byTier.heavy.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="moderate" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-500" />
            Moderate ({stats?.byTier.moderate.total || 621})
            {stats && stats.byTier.moderate.pending > 0 && (
              <Badge variant="outline">{stats.byTier.moderate.pending}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="extreme">
          {renderModulesList(extremeModules, 'extreme')}
        </TabsContent>
        
        <TabsContent value="heavy">
          {renderModulesList(heavyModules, 'heavy')}
        </TabsContent>
        
        <TabsContent value="moderate">
          {renderModulesList(moderateModules, 'moderate')}
        </TabsContent>
      </Tabs>
    </div>
  );
};
